/**
 * Parse structured API errors that include field-specific error information
 */
export interface StructuredApiError {
  code: number;
  error: string;
  message: string;
  fields?: string[];
}

export type FieldErrors = Record<string, string>;

/**
 * Focus an input field by its field name
 */
export const focusFieldByName = (fieldName: string): void => {
  // Map field names to their corresponding DOM element IDs
  const fieldIdMap: Record<string, string> = {
    // BrandInfoTab fields
    entityType: 'entityType',
    displayName: 'displayName',
    companyName: 'companyName',
    ein: 'ein',
    phone: 'phone',
    email: 'email',
    website: 'website',
    brandRelationship: 'brandRelationship',
    vertical: 'vertical',
    street: 'street',
    city: 'city',
    state: 'state',
    postalCode: 'postalCode',
    country: 'country',
    einIssuingCountry: 'einIssuingCountry',

    // CampaignInfoTab fields
    brandId: 'brandId',
    resellerId: 'resellerId',
    usecase: 'usecase',
    description: 'description',
    messageFlow: 'messageFlow',
    privacyPolicyLink: 'privacyPolicyLink',
    termsAndConditionsLink: 'termsAndConditionsLink',
    sample1: 'sample1',
    sample2: 'sample2',
    sample3: 'sample3',
    sample4: 'sample4',
    sample5: 'sample5',
    helpMessage: 'helpMessage',
    optinMessage: 'optinMessage',
    optoutMessage: 'optoutMessage',
  };

  const elementId = fieldIdMap[fieldName];
  if (elementId) {
    // Use setTimeout to ensure the DOM is updated before focusing
    setTimeout(() => {
      // Try to find the element by ID first
      let element = document.getElementById(elementId) as HTMLElement;

      // If not found by ID, try by name attribute
      if (!element) {
        element = document.querySelector(`[name="${elementId}"]`) as HTMLElement;
      }

      // If still not found, try to find by data-testid or aria-label
      if (!element) {
        element = document.querySelector(`[data-testid="${elementId}"]`) as HTMLElement;
      }

      if (element) {
        // For input elements, focus them directly
        if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
          (element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).focus();
          // Also select the text for better UX
          (element as HTMLInputElement | HTMLTextAreaElement).select();
        }
        // For Material-UI components, try to find the input inside
        else {
          // Try to find input, textarea, or contenteditable elements
          const inputElement = element.querySelector('input, textarea, [contenteditable="true"]') as HTMLElement;
          if (inputElement) {
            inputElement.focus();
            // Also select the text for better UX
            if (inputElement.tagName === 'INPUT' || inputElement.tagName === 'TEXTAREA') {
              (inputElement as HTMLInputElement | HTMLTextAreaElement).select();
            }
          } else {
            // If no input found, try to focus the element itself (for clickable elements)
            element.focus();
          }
        }

        // Scroll the element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }
};

/**
 * Generic API error type that can represent various error formats
 */
export type ApiError = StructuredApiError | {
  response?: {
    data?: StructuredApiError | {
      message?: string;
    };
  };
  message?: string;
} | unknown;

/**
 * Check if data has the structured error format
 */
const isStructuredErrorData = (data: unknown): data is StructuredApiError => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const structuredError = data as StructuredApiError;
  return (
    typeof structuredError.code === 'number' &&
    typeof structuredError.error === 'string' &&
    typeof structuredError.message === 'string' &&
    Array.isArray(structuredError.fields)
  );
};

/**
 * Extract the actual error data from an axios error response
 */
const getErrorData = (error: ApiError): StructuredApiError | { message?: string } | unknown => {
  // Check if it's a direct structured error
  if (isStructuredErrorData(error)) {
    return error;
  }

  // Check if it's an axios error with response.data containing structured error
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: unknown } };

    if (axiosError.response?.data) {
      // Check if response.data is the structured error
      if (isStructuredErrorData(axiosError.response.data)) {
        return axiosError.response.data;
      }

      // Check if response.data has the structured error nested
      if (axiosError.response.data && typeof axiosError.response.data === 'object' && 'data' in axiosError.response) {
        const nestedData = (axiosError.response as { data?: unknown }).data;
        if (nestedData && isStructuredErrorData(nestedData)) {
          return nestedData;
        }
      }

      return axiosError.response.data;
    }
  }

  return error;
};

/**
 * Check if an error response has the structured format with fields array
 */
export const isStructuredError = (error: ApiError): error is StructuredApiError => {
  const errorData = getErrorData(error);
  return isStructuredErrorData(errorData);
};

/**
 * Extract field-specific errors from a structured API error response
 */
export const parseFieldErrors = (error: ApiError): FieldErrors => {
  const errorData = getErrorData(error);

  if (!isStructuredError(errorData)) {
    return {};
  }

  const fieldErrors: FieldErrors = {};

  // If fields array is provided, map the main message to each field
  if (errorData.fields && errorData.fields.length > 0) {
    errorData.fields.forEach((field: string) => {
      fieldErrors[field] = errorData.message;
    });
  }

  return fieldErrors;
};

/**
 * Get a user-friendly error message from various error formats
 */
export const getErrorMessage = (error: ApiError): string => {
  const errorData = getErrorData(error);

  if (isStructuredError(errorData)) {
    return errorData.message;
  }

  if (errorData && typeof errorData === 'object' && 'message' in errorData) {
    return (errorData as { message: string }).message;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return (error as { message: string }).message;
  }

  return 'An unexpected error occurred';
};

/**
 * Handle API errors with support for field-specific errors
 */
export const handleApiError = (
  error: ApiError,
  setFieldErrors?: (errors: FieldErrors) => void,
  focusFirstError?: (firstErrorField: string) => void
): string => {
  const errorMessage = getErrorMessage(error);

  // If we have a function to set field errors, parse field-specific errors
  if (setFieldErrors) {
    const fieldErrors = parseFieldErrors(error);
    setFieldErrors(fieldErrors);

    // Focus the first field with an error
    if (focusFirstError && Object.keys(fieldErrors).length > 0) {
      const firstErrorField = Object.keys(fieldErrors)[0];
      focusFirstError(firstErrorField);
    }

    if (errorMessage.toLowerCase().includes('brand')) {
      const key = 'brandId';
      const brandError: FieldErrors = {};
      brandError[key] = errorMessage;
      setFieldErrors(brandError);
      focusFieldByName(key);
    }
  }

  return errorMessage;
};
